import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { cn } from "@/lib/utils";
import type { UsageRow } from "@/hooks/useAdminMetrics";

const PAGE_SIZE = 50;

export function RecentCallsTable({
  usage,
  rate,
  onExport,
}: {
  usage: UsageRow[];
  rate: number;
  onExport: (filtered: UsageRow[]) => void;
}) {
  const [callType, setCallType] = useState<string>("all");
  const [model, setModel] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [userQ, setUserQ] = useState("");
  const [page, setPage] = useState(0);

  const callTypes = useMemo(
    () => Array.from(new Set(usage.map((u) => u.call_type))).sort(),
    [usage],
  );
  const models = useMemo(
    () => Array.from(new Set(usage.map((u) => u.model))).sort(),
    [usage],
  );

  const filtered = useMemo(() => {
    return usage.filter((r) => {
      if (callType !== "all" && r.call_type !== callType) return false;
      if (model !== "all" && r.model !== model) return false;
      if (status !== "all" && r.status !== status) return false;
      if (userQ && !(r.user_id ?? "").toLowerCase().includes(userQ.toLowerCase())) return false;
      return true;
    });
  }, [usage, callType, model, status, userQ]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const slice = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base">Recent calls</CardTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onExport(filtered)}
          disabled={!filtered.length}
        >
          <Download className="h-4 w-4" />
          CSV ({filtered.length})
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Select
            value={callType}
            onValueChange={(v) => {
              setCallType(v);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-8 w-auto min-w-[140px] text-xs">
              <SelectValue placeholder="Call type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All call types</SelectItem>
              {callTypes.map((ct) => (
                <SelectItem key={ct} value={ct}>
                  {ct}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={model}
            onValueChange={(v) => {
              setModel(v);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-8 w-auto min-w-[140px] text-xs">
              <SelectValue placeholder="Model" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All models</SelectItem>
              {models.map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={status}
            onValueChange={(v) => {
              setStatus(v);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-8 w-auto min-w-[120px] text-xs">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="ok">ok</SelectItem>
              <SelectItem value="error">error</SelectItem>
            </SelectContent>
          </Select>
          <Input
            value={userQ}
            onChange={(e) => {
              setUserQ(e.target.value);
              setPage(0);
            }}
            placeholder="Filter by user id…"
            className="h-8 max-w-[260px] text-xs"
          />
        </div>

        <div className="overflow-x-auto rounded border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="h-9 text-[11px]">Time</TableHead>
                <TableHead className="h-9 text-[11px]">User</TableHead>
                <TableHead className="h-9 text-[11px]">Call</TableHead>
                <TableHead className="h-9 text-[11px]">Model</TableHead>
                <TableHead className="h-9 text-right text-[11px]">In</TableHead>
                <TableHead className="h-9 text-right text-[11px]">Out</TableHead>
                <TableHead className="h-9 text-[11px]">Status</TableHead>
                <TableHead className="h-9 text-right text-[11px]">USD</TableHead>
                <TableHead className="h-9 text-right text-[11px]">INR</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {slice.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-xs text-muted-foreground">
                    No calls match the filters.
                  </TableCell>
                </TableRow>
              ) : (
                slice.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap p-2 font-mono text-[10px] text-muted-foreground">
                      {new Date(r.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="p-2 font-mono text-[10px] text-muted-foreground">
                      {(r.user_id ?? "—").slice(0, 8)}…
                    </TableCell>
                    <TableCell className="p-2 text-[11px]">{r.call_type}</TableCell>
                    <TableCell className="p-2 text-[11px] text-muted-foreground">
                      {r.model.replace("gemini-2.5-", "")}
                    </TableCell>
                    <TableCell className="p-2 text-right text-[11px] tabular-nums">
                      {r.input_tokens.toLocaleString()}
                    </TableCell>
                    <TableCell className="p-2 text-right text-[11px] tabular-nums">
                      {r.output_tokens.toLocaleString()}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "p-2 text-[11px]",
                        r.status === "ok" ? "text-emerald-600" : "text-destructive",
                      )}
                    >
                      {r.status}
                    </TableCell>
                    <TableCell className="p-2 text-right text-[11px] font-medium tabular-nums">
                      ${Number(r.estimated_cost_usd ?? 0).toFixed(6)}
                    </TableCell>
                    <TableCell className="p-2 text-right text-[11px] tabular-nums text-muted-foreground">
                      ₹{(Number(r.estimated_cost_usd ?? 0) * rate).toFixed(4)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>
            Page {safePage + 1} of {pageCount} · {filtered.length.toLocaleString()} rows
          </span>
          <div className="flex gap-1">
            <Button
              size="icon"
              variant="outline"
              className="h-7 w-7"
              disabled={safePage === 0}
              onClick={() => setPage(safePage - 1)}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              className="h-7 w-7"
              disabled={safePage >= pageCount - 1}
              onClick={() => setPage(safePage + 1)}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
